import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types';
import { api } from '@/utils/api';
import { API_ENDPOINTS } from '@/utils/constants';
import { useUIStore } from './uiStore';

/** Apply the user's persisted accent color to the UI (no-op for guests). */
const applyUserAccent = (user: User | null) => {
    if (user?.accent_color) {
        useUIStore.getState().setAccentColor(user.accent_color);
    }
};

interface AuthState {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;

    setUser: (user: User | null) => void;
    setIsLoading: (isLoading: boolean) => void;
    setError: (error: string | null) => void;
    login: (email: string, password: string) => Promise<void>;
    register: (data: { name: string; username: string; email: string; password: string; password_confirmation: string; newsletter?: boolean }) => Promise<void>;
    logout: () => Promise<void>;
    refreshToken: () => Promise<void>;
    fetchUser: () => Promise<void>;
    updateUser: (data: Partial<User>) => Promise<void>;
    clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            token: localStorage.getItem('auth_token') || null,
            isAuthenticated: !!localStorage.getItem('auth_token'),
            isLoading: false,
            error: null,

            login: async (email: string, password: string) => {
                set({ isLoading: true, error: null });
                try {
                    const response = (await api.post(API_ENDPOINTS.auth.login, { email, password })) as any;
                    // The API interceptor unwraps the axios envelope, so `response`
                    // is the JSON body. Some endpoints nest the payload under `data`.
                    const payload = response?.data ?? response;
                    const { user, access_token, token_type, expires_at } = payload;
                    localStorage.setItem('auth_token', access_token);
                    localStorage.setItem('auth_token_type', token_type);
                    if (expires_at) {
                        localStorage.setItem('token_expires_at', String(new Date(expires_at).getTime()));
                    }
                    set({ user, token: access_token, isAuthenticated: true, error: null });
                    applyUserAccent(user);
                } catch (error: unknown) {
                    const err = error as { response?: { data?: { message?: string } } };
                    set({ error: err.response?.data?.message || 'Login failed' });
                    throw err;
                } finally {
                    set({ isLoading: false });
                }
            },

            register: async ({ name, username, email, password, password_confirmation, newsletter }: { name: string; username: string; email: string; password: string; password_confirmation: string; newsletter?: boolean }) => {
                set({ isLoading: true, error: null });
                try {
                    const response = (await api.post(API_ENDPOINTS.auth.register, {
                        name,
                        username,
                        email,
                        password,
                        password_confirmation,
                        ...(newsletter !== undefined ? { newsletter } : {}),
                    })) as any;
                    const payload = response?.data ?? response;
                    const { user, access_token, token_type, expires_at } = payload;
                    localStorage.setItem('auth_token', access_token);
                    localStorage.setItem('auth_token_type', token_type);
                    if (expires_at) {
                        localStorage.setItem('token_expires_at', String(new Date(expires_at).getTime()));
                    }
                    set({ user, token: access_token, isAuthenticated: true, error: null });
                    applyUserAccent(user);
                } catch (error: unknown) {
                    const err = error as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } };
                    set({ error: err.response?.data?.message || 'Registration failed' });
                    throw err;
                } finally {
                    set({ isLoading: false });
                }
            },

            logout: async () => {
                try {
                    await api.post(API_ENDPOINTS.auth.logout);
                } catch {
                    // Ignore logout errors
                }
                localStorage.removeItem('auth_token');
                localStorage.removeItem('auth_token_type');
                localStorage.removeItem('token_expires_at');
                // Drop the persisted Zustand snapshot so a stale user can
                // never be restored on the next load. The `set` below then
                // re-persists a clean, fully-logged-out state.
                localStorage.removeItem('auth-storage');
                set({ user: null, token: null, isAuthenticated: false });
                // Reset the accent to the default so anonymous views use the
                // global theme instead of a stale per-user override.
                useUIStore.getState().setAccentColor('blue');
                // Redirect to the landing page so logout is visible to the user.
                // A full navigation also guarantees any in-memory auth state
                // (e.g. chat/stream sockets keyed on the old user) is torn down.
                if (window.location.pathname !== '/') {
                    window.location.assign('/');
                }
            },

            refreshToken: async () => {
                try {
                    const response = await api.post(API_ENDPOINTS.auth.refresh);
                    const { access_token, token_type, expires_at } = response.data;
                    localStorage.setItem('auth_token', access_token);
                    localStorage.setItem('auth_token_type', token_type);
                    if (expires_at) {
                        localStorage.setItem('token_expires_at', String(new Date(expires_at).getTime()));
                    }
                    set({ token: access_token });
                } catch {
                    await get().logout();
                }
            },

            fetchUser: async () => {
                set({ isLoading: true });
                try {
                    const response = (await api.get(API_ENDPOINTS.auth.me)) as any;
                    // The API interceptor unwraps the axios envelope, so `response`
                    // is the JSON body. /v1/user nests the payload under `user`.
                    const user = response?.user ?? response?.data?.user ?? response?.data ?? (response?.success ? null : response);
                    set({ user: user ?? null, isAuthenticated: true });
                    // The server value is authoritative — re-apply it so the
                    // accent survives refreshes and login/logout cycles.
                    applyUserAccent(user ?? null);
                } catch {
                    // The session is invalid — clear the stale token so the
                    // API interceptor stops sending it.
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('auth_token_type');
                    localStorage.removeItem('token_expires_at');
                    set({ user: null, token: null, isAuthenticated: false });
                } finally {
                    set({ isLoading: false });
                }
            },

            updateUser: async (data: Partial<User>) => {
                try {
                    const response = (await api.put(API_ENDPOINTS.auth.profile, data)) as any;
                    const payload = response?.data ?? response;
                    set({ user: payload?.user ?? payload });
                } catch (error: unknown) {
                    const err = error as { response?: { data?: { message?: string } } };
                    set({ error: err.response?.data?.message || 'Update failed' });
                    throw err;
                }
            },

            clearError: () => set({ error: null }),

            setUser: (user) => set({ user }),
            setIsLoading: (isLoading) => set({ isLoading }),
            setError: (error) => set({ error }),
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                token: state.token,
                isAuthenticated: state.isAuthenticated,
                user: state.user,
            }),
            merge: (persistedState, currentState) => {
                const persisted = persistedState as Partial<AuthState>;
                // Reconcile with the raw localStorage token so a stale
                // persisted snapshot can never log the user out on reload.
                const rawToken = localStorage.getItem('auth_token');
                if (!rawToken) {
                    // No live token — never restore a stale persisted session.
                    // This guarantees fresh guest sessions start clean even if
                    // an old `auth-storage` snapshot still holds a user.
                    return {
                        ...currentState,
                        user: null,
                        token: null,
                        isAuthenticated: false,
                    };
                }
                return {
                    ...currentState,
                    ...persisted,
                    token: rawToken ?? persisted.token ?? currentState.token,
                    isAuthenticated: true,
                };
            },
        }
    )
);

// Restore the user session on app load when a token is present. The SPA
// authenticates via Sanctum Bearer tokens in localStorage, so after a full
// page reload we re-fetch the user to repopulate `user` (not persisted
// reliably across versions) and validate the token.
if (localStorage.getItem('auth_token')) {
    useAuthStore.getState().fetchUser();
}
